import { Button, FormatByte, HStack, Text, VStack } from '@chakra-ui/react';
import { EMAIL_ATTACHMENT_LIMITS, type BatchAttachmentInfo } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import type { EmailAttachmentIssue } from '@/hooks/email-attachment-draft';
import AttachmentChips from './AttachmentChips';

export interface EmailAttachmentControlProps {
  attachments: BatchAttachmentInfo[];
  onFileSelect: (files: FileList | null) => void;
  onRemoveAttachment: (id: string) => void;
  isUploading: boolean;
  deletingId: string | null;
  issues?: EmailAttachmentIssue[];
  onDismissIssue?: (id: string) => void;
  validationError?: string | null;
  maxTotalBytes?: number;
  disabled?: boolean;
}

export interface EmailAttachmentsFieldProps
  extends Omit<EmailAttachmentControlProps, 'onFileSelect'> {
  onRetry: () => void;
}

/** Files and operation feedback stay beneath the editor; its toolbar owns the picker. */
export default function EmailAttachmentsField({
  attachments,
  onRetry,
  onRemoveAttachment,
  isUploading,
  deletingId,
  issues = [],
  onDismissIssue,
  validationError,
  maxTotalBytes = EMAIL_ATTACHMENT_LIMITS.maxTotalBytes,
  disabled,
}: EmailAttachmentsFieldProps) {
  const { t } = useTranslation();
  return (
    <VStack align="stretch" gap={2} flexShrink={0} py={2}>
      <Text fontSize="xs" color="fg.muted">
        {t('bulkEmail.modal.attachments.limits', {
          count: EMAIL_ATTACHMENT_LIMITS.maxCount,
          file: EMAIL_ATTACHMENT_LIMITS.maxFileBytes / 1024 / 1024,
          total: maxTotalBytes / 1024 / 1024,
        })}
      </Text>
      {isUploading && (
        <Text role="status" fontSize="xs" color="fg.muted">
          {t('bulkEmail.modal.attachments.uploading')}
        </Text>
      )}
      {attachments.length > 0 && (
        <Text fontSize="xs" color="fg.muted">
          {t('bulkEmail.modal.attachments.total')}:{' '}
          <FormatByte
            unitSystem="binary"
            value={attachments.reduce((sum, file) => sum + file.size, 0)}
          />
        </Text>
      )}
      <AttachmentChips
        attachments={attachments}
        onRemove={onRemoveAttachment}
        deletingId={deletingId}
        variant="plain"
        disabled={disabled || isUploading || !!deletingId}
      />
      {validationError && (
        <Text role="alert" fontSize="sm" color="fg.error">
          {t(validationError)}
        </Text>
      )}
      {issues.map((issue) => (
        <HStack key={issue.id} justify="space-between" align="start">
          <Text
            role="alert"
            fontSize="sm"
            color="fg.error"
            minW={0}
            flex={1}
            overflowWrap="anywhere"
          >
            {issue.name}: {t(issue.messageKey)}
          </Text>
          <HStack gap={1} flexShrink={0}>
            {!issue.attachmentId && (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                disabled={disabled || isUploading || !!deletingId}
                onClick={onRetry}
              >
                {t('common.retry')}
              </Button>
            )}
            <Button
              type="button"
              size="xs"
              variant="ghost"
              disabled={disabled || isUploading || !!deletingId}
              onClick={() =>
                issue.attachmentId
                  ? onRemoveAttachment(issue.attachmentId)
                  : onDismissIssue?.(issue.id)
              }
            >
              {t(issue.attachmentId ? 'common.retry' : 'bulkEmail.modal.attachments.remove')}
            </Button>
          </HStack>
        </HStack>
      ))}
    </VStack>
  );
}
